import { BankAccountType } from './constants/clients';
import { ContractPaymentForm } from './constants/contracts';

export const capitalizeCardBrand = brand => {
  if (!brand) return brand;
  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
};

export const formatBankAccountDisplay = (account, translateBankAccountType) => {
  if (!account) return '';

  const parts = [];

  if (
    account.bankAccountType &&
    account.bankAccountType !== BankAccountType.CLABE
  ) {
    const translatedType = translateBankAccountType(account.bankAccountType);
    if (translatedType) {
      parts.push(translatedType);
    }
  }

  if (account.bankName) {
    parts.push(account.bankName);
  }

  if (account.bankAccountNumber || account.accountNumber) {
    parts.push(account.bankAccountNumber || account.accountNumber);
  }

  return parts.length > 0 ? parts.join(' - ') : `Cuenta bancaria ${account.id}`;
};

export const formatCreditCardDisplay = card => {
  if (!card) return '';

  const parts = [];

  if (card.lastFourDigits || card.last4Digits) {
    parts.push(`****${card.lastFourDigits || card.last4Digits}`);
  }

  if (card.brand) {
    parts.push(capitalizeCardBrand(card.brand));
  }

  if (card.bank) {
    parts.push(`(${card.bank})`);
  }

  return parts.join(' - ');
};

export const formatBankAccountDetails = account => {
  if (!account) return '';

  const { bankName } = account;
  const { bankAccountNumber } = account;

  return `${bankName} (${bankAccountNumber})`;
};

export const formatCreditCardDetails = card => {
  if (!card) return '';

  const parts = [];
  if (card.lastFourDigits || card.last4Digits) {
    parts.push(`****${card.lastFourDigits || card.last4Digits}`);
  }
  if (card.brand) {
    parts.push(capitalizeCardBrand(card.brand));
  }
  if (card.bank) {
    parts.push(card.bank);
  }
  return parts.join(' — ');
};

export const extractElevenDigitAccountFromClabe = clabe =>
  clabe.length >= 17 ? clabe.substring(6, 17) : '';

export const formatPaymentMethodDisplay = ({
  paymentForm,
  bankAccount,
  creditCard,
  t
}) => {
  if (!paymentForm) {
    return t('CONTRACT_VIEW.CONTRACT_DETAIL.NO_PAYMENT_METHOD_SELECTED');
  }

  if (paymentForm === ContractPaymentForm.AUTOMATIC_DEBIT) {
    if (bankAccount) {
      return `${t(
        'CONTRACT_VIEW.CONTRACT_DETAIL.DOMICILED_COLLECTION'
      )}: ${formatBankAccountDetails(bankAccount)}`;
    }
    return t('CONTRACT_VIEW.CONTRACT_DETAIL.DOMICILED_COLLECTION');
  }

  if (paymentForm === ContractPaymentForm.CREDIT_CARD) {
    if (creditCard) {
      return `${t(
        'CONTRACT_VIEW.CONTRACT_DETAIL.CREDIT_CARD'
      )}: ${formatCreditCardDetails(creditCard)}`;
    }
    return t('CONTRACT_VIEW.CONTRACT_DETAIL.CREDIT_CARD');
  }

  if (paymentForm === ContractPaymentForm.BANK_TRANSFER) {
    return t('CONTRACT_VIEW.CONTRACT_DETAIL.TRANSFER');
  }

  return paymentForm;
};

export const translateBankAccountType = (type, t) => {
  switch (type) {
    case BankAccountType.SANTANDER_CHECKS:
      return t('ENUMS.ACCOUNT_TYPE.SANTANDER_CHECKS');
    case BankAccountType.DEBIT_CARD:
      return t('ENUMS.ACCOUNT_TYPE.DEBIT_CARD');
    case BankAccountType.CLABE:
      return t('ENUMS.ACCOUNT_TYPE.CLABE');
    default:
      return '';
  }
};

export const getPaymentFormOptions = () => [
  {
    key: ContractPaymentForm.AUTOMATIC_DEBIT,
    name: 'CONTRACT_VIEW.CONTRACT_DETAIL.DOMICILED_COLLECTION'
  },
  {
    key: ContractPaymentForm.CREDIT_CARD,
    name: 'CONTRACT_VIEW.CONTRACT_DETAIL.CREDIT_CARD'
  },
  {
    key: ContractPaymentForm.BANK_TRANSFER,
    name: 'CONTRACT_VIEW.CONTRACT_DETAIL.TRANSFER'
  }
];

export const getBankAccountOptions = (validBankAccounts, t) => {
  return validBankAccounts.map(account => ({
    key: account.id,
    name: formatBankAccountDisplay(account, type =>
      translateBankAccountType(type, t)
    )
  }));
};

export const getSecondaryBankAccountOptions = (
  validBankAccounts,
  primaryBankAccountId,
  t
) => {
  return validBankAccounts
    .filter(account => account.id !== primaryBankAccountId)
    .map(account => ({
      key: account.id,
      name: formatBankAccountDisplay(account, type =>
        translateBankAccountType(type, t)
      )
    }));
};

export const getCreditCardOptions = creditCards => {
  return creditCards.map(card => ({
    key: card.id,
    name: formatCreditCardDisplay(card)
  }));
};

export const calculateDefaultPaymentForm = (
  existingPaymentForm,
  validBankAccountsCount
) => {
  if (existingPaymentForm) {
    return existingPaymentForm;
  }
  return validBankAccountsCount > 0
    ? ContractPaymentForm.AUTOMATIC_DEBIT
    : ContractPaymentForm.BANK_TRANSFER;
};

export const calculateDefaultBankAccountId = (
  existingBankAccountId,
  paymentForm,
  validBankAccounts,
  getFirstBankAccountId
) => {
  if (existingBankAccountId) {
    return existingBankAccountId;
  }
  return paymentForm === ContractPaymentForm.AUTOMATIC_DEBIT &&
    validBankAccounts.length > 0
    ? getFirstBankAccountId(validBankAccounts)
    : null;
};

export const calculateDefaultCardId = (
  existingCardId,
  paymentForm,
  creditCards,
  getFirstCreditCardId
) => {
  if (existingCardId) {
    return existingCardId;
  }
  return paymentForm === ContractPaymentForm.CREDIT_CARD &&
    creditCards.length > 0
    ? getFirstCreditCardId(creditCards)
    : null;
};

export const canSubmitPaymentMethod = ({
  paymentForm,
  bankAccountId,
  cardId,
  creditCardsCount,
  hasSantanderClabe
}) => {
  if (paymentForm === ContractPaymentForm.AUTOMATIC_DEBIT) {
    return !!bankAccountId;
  }
  if (paymentForm === ContractPaymentForm.CREDIT_CARD) {
    return creditCardsCount > 0 && !!cardId;
  }
  if (paymentForm === ContractPaymentForm.BANK_TRANSFER) {
    return hasSantanderClabe;
  }
  return false;
};

export const hasPaymentDetailsToShow = ({
  paymentForm,
  selectedBankAccount,
  selectedCard,
  clabe
}) => {
  if (!paymentForm) {
    return false;
  }

  if (paymentForm === ContractPaymentForm.AUTOMATIC_DEBIT) {
    return !!selectedBankAccount;
  }

  if (paymentForm === ContractPaymentForm.CREDIT_CARD) {
    return !!selectedCard;
  }

  if (paymentForm === ContractPaymentForm.BANK_TRANSFER) {
    return !!clabe;
  }

  return false;
};

export const hasNoPaymentMethodSelected = ({
  paymentForm,
  automaticDebitType,
  creditCardType,
  bankTransferType,
  selectedBankAccountId,
  selectedCardId
}) => {
  if (!paymentForm || paymentForm === '') {
    return true;
  }

  if (paymentForm === automaticDebitType && !selectedBankAccountId) {
    return true;
  }

  if (paymentForm === creditCardType && !selectedCardId) {
    return true;
  }

  if (
    paymentForm !== automaticDebitType &&
    paymentForm !== creditCardType &&
    paymentForm !== bankTransferType
  ) {
    return true;
  }

  return false;
};

export const getPaymentMethodDisplayText = (paymentForm, t) => {
  if (paymentForm === ContractPaymentForm.AUTOMATIC_DEBIT) {
    return t('CONTRACT_VIEW.CONTRACT_DETAIL.DOMICILED_COLLECTION');
  }
  if (paymentForm === ContractPaymentForm.CREDIT_CARD) {
    return t('CONTRACT_VIEW.CONTRACT_DETAIL.CREDIT_CARD');
  }
  if (paymentForm === ContractPaymentForm.BANK_TRANSFER) {
    return t('CONTRACT_VIEW.CONTRACT_DETAIL.TRANSFER');
  }
  return t('CONTRACT_VIEW.CONTRACT_DETAIL.NO_PAYMENT_METHOD_SELECTED');
};
